import { useMemo } from 'react';
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  useReactFlow,
} from '@xyflow/react';
import type { EdgeProps } from '@xyflow/react';
import { X } from 'lucide-react';
import { useFlowBuilderStore } from '../../stores/flowBuilderStore';

export function CustomEdge({
  id,
  source,
  target,
  sourceHandleId,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style = {},
  markerEnd,
  selected,
}: EdgeProps) {
  const { setEdges } = useReactFlow();
  const edges = useFlowBuilderStore((state) => state.edges);
  // edges touching a selected node light up (mockup: 'active')
  const active = useFlowBuilderStore((state) =>
    state.nodes.some((node) => node.selected && (node.id === source || node.id === target)),
  );
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
    curvature: 0.35,
  });
  const orderLabel = useMemo(() => {
    const normalizedHandle = sourceHandleId || '__default__';
    const sameOutputEdges = edges.filter(
      (edge) => edge.source === source && (edge.sourceHandle || '__default__') === normalizedHandle,
    );

    if (sameOutputEdges.length <= 1) {
      return null;
    }

    const edgeIndex = sameOutputEdges.findIndex((edge) => edge.id === id);
    if (edgeIndex < 0) {
      return null;
    }

    return edgeIndex + 1;
  }, [edges, id, source, sourceHandleId]);
  const orderX = sourceX + (targetX - sourceX) * 0.28;
  const orderY = sourceY + (targetY - sourceY) * 0.28;

  const onEdgeClick = (evt: React.MouseEvent) => {
    evt.stopPropagation();
    setEdges((prevEdges) => prevEdges.filter((e) => e.id !== id));
  };

  return (
    <>
      <BaseEdge
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: selected ? '#93c5fd' : active ? 'var(--fb-accent, #3b82f6)' : 'var(--fb-edge, #64748b)',
          strokeWidth: selected ? 3 : active ? 2.5 : 1.75,
          strokeDasharray: '0',
          transition: 'stroke 120ms ease',
          ...style,
        }}
      />
      <EdgeLabelRenderer>
        {orderLabel && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${orderX}px,${orderY}px)`,
              pointerEvents: 'none',
            }}
            className="nodrag nopan"
            title={`Ordem de execução: ${orderLabel}`}
          >
            <div className="min-w-[18px] h-[18px] px-1 bg-slate-900 text-violet-200 border border-violet-500 rounded-full text-[10px] font-semibold flex items-center justify-center shadow-md">
              {orderLabel}
            </div>
          </div>
        )}
        {selected && (
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              fontSize: 12,
              pointerEvents: 'all',
            }}
            className="nodrag nopan"
          >
            <button
              className="w-[22px] h-[22px] bg-slate-900 border border-violet-500 rounded-full flex items-center justify-center text-slate-200 hover:text-red-300 hover:border-red-400 hover:bg-slate-800 transition-colors shadow-lg"
              onClick={onEdgeClick}
              title="Remover conexão"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </EdgeLabelRenderer>
    </>
  );
}
